import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Bug, Lightbulb, Sparkles, Heart, Send, CheckCircle } from 'lucide-react';

const TYPES = [
  { id: 'bug', label: 'Bug', icon: Bug, color: 'text-red-400', bg: 'bg-red-400/10', border: 'border-red-400/30' },
  { id: 'feature', label: 'Feature Idea', icon: Lightbulb, color: 'text-amber-400', bg: 'bg-amber-400/10', border: 'border-amber-400/30' },
  { id: 'accuracy', label: 'Accuracy Issue', icon: Sparkles, color: isDark => isDark ? 'text-[#7662ac]' : 'text-[#586f8d]', bg: isDark => isDark ? 'bg-[#7662ac]/10' : 'bg-[#586f8d]/10', border: isDark => isDark ? 'border-[#7662ac]/30' : 'border-[#586f8d]/30' },
  { id: 'other', label: 'Something Else', icon: Heart, color: 'text-pink-400', bg: 'bg-pink-400/10', border: 'border-pink-400/30' },
];

const PRIORITIES = [
  { id: 'low', label: 'Nice to have' },
  { id: 'medium', label: 'Important' },
  { id: 'high', label: 'Urgent' },
];

const Feedback = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [form, setForm] = useState({ type: '', priority: 'medium', message: '', page: '', name: '', email: user?.email || '' });
  const [submitted, setSubmitted] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const cardClass = `rounded-lg border ${isDark ? 'bg-[#121212] border-[#27272a]' : 'bg-white border-[#e5e5e5]'}`;
  const inputClass = `w-full px-4 py-2.5 text-sm rounded-lg border ${isDark ? 'bg-[#18181b] border-[#27272a] text-white placeholder-[#52525b]' : 'bg-white border-[#e5e5e5] text-[#1a1a1a] placeholder-[#999]'}`;

  const handleSubmit = () => {
    if (!form.message.trim()) return;
    console.log('Feedback submitted:', form);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setForm({ type: '', priority: 'medium', message: '', page: '', name: '', email: user?.email || '' });
    }, 3000);
  };

  if (submitted) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center animate-fade-in">
          <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-emerald-400/10`}>
            <CheckCircle className="h-8 w-8 text-emerald-400" />
          </div>
          <h2 className={`text-lg font-semibold font-[Manrope] mb-1 ${isDark ? 'text-white' : 'text-[#1a1a1a]'}`}>Thanks for the feedback!</h2>
          <p className={`text-sm ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>We read every note.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className={`${cardClass} p-6 accent-gradient`}>
        <div className="flex items-center gap-3 mb-2">
          <Heart className="h-5 w-5 text-white" />
          <h1 className="text-lg font-semibold font-[Manrope] text-white">Send Feedback</h1>
        </div>
        <p className="text-sm text-white/70">Help us improve TutorTracer. Bug reports, feature ideas, or just a note — we appreciate it all.</p>
      </div>

      {/* Type */}
      <div>
        <label className={`text-xs font-medium block mb-2 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>What kind of feedback?</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {TYPES.map(t => {
            const color = typeof t.color === 'function' ? t.color(isDark) : t.color;
            const bg = typeof t.bg === 'function' ? t.bg(isDark) : t.bg;
            const border = typeof t.border === 'function' ? t.border(isDark) : t.border;
            const selected = form.type === t.id;
            return (
              <button
                key={t.id}
                onClick={() => set('type', t.id)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                  selected ? `${border} ${bg} ${color}` : isDark ? 'border-[#27272a] text-[#a1a1aa] hover:border-[#3f3f46]' : 'border-[#e5e5e5] text-[#666] hover:border-[#d0d0d0]'
                }`}
              >
                <t.icon className={`h-4 w-4 ${selected ? color : isDark ? 'text-[#52525b]' : 'text-[#999]'}`} strokeWidth={1.5} />
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Priority */}
      <div>
        <label className={`text-xs font-medium block mb-2 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Priority</label>
        <div className="flex gap-2">
          {PRIORITIES.map(p => (
            <button
              key={p.id}
              onClick={() => set('priority', p.id)}
              className={`px-4 py-2 text-xs rounded-lg border transition-colors ${
                form.priority === p.id
                  ? isDark ? 'bg-[#18181b] border-[#3f3f46] text-white' : 'bg-[#e8e8e8] border-[#d0d0d0] text-[#1a1a1a]'
                  : isDark ? 'border-[#27272a] text-[#52525b] hover:text-[#a1a1aa]' : 'border-[#e5e5e5] text-[#999] hover:text-[#666]'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Which page */}
      <div>
        <label className={`text-xs font-medium block mb-1.5 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Which page? <span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>(optional)</span></label>
        <input value={form.page} onChange={e => set('page', e.target.value)} placeholder="e.g. Dashboard, Issues" className={inputClass} />
      </div>

      {/* Message */}
      <div>
        <label className={`text-xs font-medium block mb-1.5 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Tell us more <span className="text-red-400">*</span></label>
        <textarea
          value={form.message}
          onChange={e => set('message', e.target.value)}
          placeholder="Describe the issue, idea, or feedback..."
          rows={5}
          className={`${inputClass} resize-none`}
        />
      </div>

      {/* Name + Email */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className={`text-xs font-medium block mb-1.5 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Name <span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>(optional)</span></label>
          <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Your name" className={inputClass} />
        </div>
        <div>
          <label className={`text-xs font-medium block mb-1.5 ${isDark ? 'text-[#a1a1aa]' : 'text-[#666]'}`}>Email <span className={isDark ? 'text-[#52525b]' : 'text-[#999]'}>(optional)</span></label>
          <input value={form.email} onChange={e => set('email', e.target.value)} placeholder="For follow-up" className={inputClass} />
        </div>
      </div>

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={!form.message.trim()}
        className={`flex items-center gap-2 px-6 py-2.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-40 ${isDark ? 'bg-white text-black hover:bg-[#e5e5e5]' : 'bg-[#1a1a1a] text-white hover:bg-[#333]'}`}
      >
        <Send className="h-4 w-4" /> Send Feedback
      </button>
    </div>
  );
};

export default Feedback;
